import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  LineController,
  Legend,
  Tooltip,
  Title as ChartTitle,
} from "chart.js";
import type { TooltipItem } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import type { Context as DataLabelContext } from "chartjs-plugin-datalabels";

// Register necessary scales and elements for bar charts
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  LineController,
  Legend,
  Tooltip,
  ChartTitle,
  ChartDataLabels,
);

export interface BarChartDataset {
  label: string;
  data: Array<number | null>;
  type?: "bar" | "line";

  // Render order (higher draws on top)
  order?: number;

  // Bar styling
  backgroundColor?: string | string[];

  // Line styling
  borderColor?: string;
  borderWidth?: number;
  tension?: number;
  fill?: boolean;
  pointRadius?: number;
  pointBackgroundColor?: string;

  // Axis assignment (e.g. "y" or "y1")
  yAxisID?: "y" | "y1";

  // Per-dataset formatting
  valueFormatter?: (value: number) => string; // tooltips
  dataLabelFormatter?: (value: number) => string; // labels rendered on chart

  // ChartDataLabels per-dataset overrides (passed through to plugin)
  datalabels?: Record<string, unknown>;
}

interface BarChartProps {
  labels: string[];
  datasets: BarChartDataset[];
  title?: string;
  stacked?: boolean;
  horizontal?: boolean;
  showLegend?: boolean;
  showDataLabels?: boolean;
  height?: string; // Tailwind height class (e.g., 'h-96', 'h-full')
  valueFormatter?: (value: number) => string;
}

type ChartDatum = number | string | null | undefined;
type DatasetValueFormatter = (value: number) => string;

interface ChartValueDataset {
  data: ChartDatum[];
  type?: "bar" | "line";
  yAxisID?: "y" | "y1";
  valueFormatter?: DatasetValueFormatter;
  dataLabelFormatter?: DatasetValueFormatter;
  borderColor?: string;
}

const toFiniteNumber = (value: unknown): number | null => {
  const parsedValue = typeof value === "string" ? parseFloat(value) : value;
  return typeof parsedValue === "number" && Number.isFinite(parsedValue)
    ? parsedValue
    : null;
};

const chartDatasets = (context: DataLabelContext): ChartValueDataset[] =>
  context.chart.data.datasets as unknown as ChartValueDataset[];

const chartDataset = (context: DataLabelContext): ChartValueDataset =>
  context.dataset as unknown as ChartValueDataset;

const numericValues = (datasets: ChartValueDataset[]): number[] =>
  datasets.flatMap((dataset) =>
    dataset.data
      .map(toFiniteNumber)
      .filter((value): value is number => value !== null),
  );

const globalMaxValue = (context: DataLabelContext): number => {
  const values = numericValues(chartDatasets(context));
  return values.length > 0 ? Math.max(...values) : 0;
};

const currentDataLabelValue = (context: DataLabelContext): number | null =>
  toFiniteNumber(chartDataset(context).data[context.dataIndex]);

const isBarDataset = (dataset: ChartValueDataset): boolean =>
  dataset.type === "bar" || dataset.type === undefined;

const primaryBarValuesAtIndex = (
  context: DataLabelContext,
  dataIndex: number,
): number[] =>
  chartDatasets(context)
    .filter(isBarDataset)
    .filter((dataset) => dataset.yAxisID !== "y1")
    .map((dataset) => toFiniteNumber(dataset.data[dataIndex]))
    .filter((value): value is number => value !== null);

const lineOverlapAtIndex = (
  context: DataLabelContext,
): { pixelDistance: number; lineIsAboveBar: boolean } | null => {
  const dataIndex = context.dataIndex;
  const lineValue = currentDataLabelValue(context);
  if (lineValue === null) {
    return null;
  }

  const barValues = primaryBarValuesAtIndex(context, dataIndex);
  const maxBarValue = barValues.length > 0 ? Math.max(...barValues) : 0;
  const yScale = context.chart.scales.y;
  const y1Scale = context.chart.scales.y1;

  if (!yScale || !y1Scale) {
    return null;
  }

  const barPixelY = yScale.getPixelForValue(maxBarValue);
  const linePixelY = y1Scale.getPixelForValue(lineValue);
  return {
    pixelDistance: Math.abs(linePixelY - barPixelY),
    lineIsAboveBar: linePixelY < barPixelY,
  };
};

/**
 * Render a configurable bar chart.  Supports both vertical and horizontal
 * orientations, stacked or grouped data, and optional titles.  This
 * component centralises Chart.js configuration so the rest of the app
 * stays clean.
 */
export const BarChart: React.FC<BarChartProps> = ({
  labels,
  datasets,
  title,
  stacked = false,
  showDataLabels = true,
  horizontal = false,
  showLegend = true,
  height = "h-56",
  valueFormatter,
}) => {
  const defaultValueFormatter = (value: number): string => {
    if (value >= 1000) {
      return "$" + (value / 1000).toFixed(1) + "k";
    } else if (value >= 100) {
      return "$" + Math.round(value).toString();
    } else if (value >= 10) {
      return "$" + value.toFixed(1);
    } else {
      return "$" + value.toFixed(2);
    }
  };

  const formatValue = valueFormatter ?? defaultValueFormatter;

  // Determine if bars will be too narrow for labels
  // For vertical charts, many labels means narrow bars
  const hasNarrowBars = !horizontal && labels.length > 15;
  const hasManyDataPoints = labels.length > 25;

  const usesSecondaryAxis = datasets.some((dataset) => dataset.yAxisID === "y1");
  const hasLineSeries = datasets.some((dataset) => dataset.type === "line");

  // Disable data labels entirely when there are too many data points to avoid overlap
  // Threshold: >20 data points for vertical charts, >10 for horizontal
  const shouldShowDataLabels = horizontal
    ? labels.length <= 10
    : labels.length <= 20;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? "y" : "x",
    layout: {
      padding: {
        left: 20,
        right: 40,
        // Add top padding when labels are positioned above bars/line points
        top: hasNarrowBars ? 30 : hasLineSeries ? 24 : 10,
      },
    },
    // Add rounded corners to the top of bars
    borderRadius: {
      topLeft: 5,
      topRight: 5,
      bottomRight: 5,
    },
    scales: {
      x: {
        stacked,
        title: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          color: "#6B7280", // tailwind gray-500 to ensure visibility in both light and dark
        },
      },
      y: {
        stacked,
        title: {
          display: false,
        },
        grid: {
          borderDash: [2, 2],
        },
        ticks: {
          color: "#6B7280",
        },
      },
      ...(usesSecondaryAxis
        ? {
            y1: {
              position: "right" as const,
              grid: {
                drawOnChartArea: false,
              },
              ticks: {
                color: "#6B7280",
                callback: function (value: string | number) {
                  const num = toFiniteNumber(value);
                  if (!Number.isFinite(num)) return "";
                  return `${num}%`;
                },
              },
            },
          }
        : {}),
    },
    plugins: {
      legend: {
        display: showLegend,
        position: "bottom" as const,
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          color: "#6B7280",
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<"bar">) {
            const label = context.dataset.label || "";
            const datasetFormatter = (context.dataset as ChartValueDataset)
              .valueFormatter;

            // Handle both horizontal (context.parsed.x) and vertical (context.parsed.y) charts
            const parsedValue = context.parsed as unknown;
            const value = horizontal
              ? typeof parsedValue === "object" && parsedValue !== null
                ? (parsedValue as { x?: ChartDatum }).x
                : (parsedValue as ChartDatum)
              : typeof parsedValue === "object" && parsedValue !== null
                ? (parsedValue as { y?: ChartDatum }).y
                : (parsedValue as ChartDatum);

            const numericValue = toFiniteNumber(value);
            if (numericValue !== null) {
              const formatter = datasetFormatter ?? formatValue;
              return `${label}: ${formatter(numericValue)}`;
            }

            return `${label}: ${value}`;
          },
        },
      },
      datalabels: {
        display: showDataLabels && shouldShowDataLabels,
        color: function (context: DataLabelContext) {
          const dataset = chartDataset(context);
          if (dataset.type === "line") {
            return dataset.borderColor ?? "#111827";
          }

          const value = currentDataLabelValue(context);
          if (value === null) {
            return "#6B7280";
          }

          const globalMax = globalMaxValue(context);
          const isInside = value > globalMax * 0.8;
          return isInside ? "#ffffff" : "#6B7280";
        },
        font: function (context: DataLabelContext) {
          if (chartDataset(context).type === "line") {
            return { weight: "bold", size: 10 };
          }

          return {
            weight: hasManyDataPoints ? "normal" : "bold",
            size: hasManyDataPoints ? 10 : 14,
          };
        },
        formatter: function (value: unknown, context: DataLabelContext) {
          const dataset = chartDataset(context);
          const datasetTooltipFormatter = dataset.valueFormatter;
          const datasetLabelFormatter = dataset.dataLabelFormatter;

          const numValue = toFiniteNumber(value);
          if (numValue === null) {
            return "";
          }

          const formatter =
            datasetLabelFormatter ?? datasetTooltipFormatter ?? formatValue;
          return formatter(numValue);
        },
        // Dynamic positioning based on bar size
        anchor: function (context: DataLabelContext) {
          if (chartDataset(context).type === "line") return "end";

          const value = currentDataLabelValue(context);
          if (value === null) {
            return "end";
          }

          return "end";
        },
        align: function (context: DataLabelContext) {
          if (chartDataset(context).type === "line") {
            // Check if line label would overlap with bar labels at this data point
            // by comparing line value position to bar values at the same index
            const overlap = lineOverlapAtIndex(context);
            if (overlap) {
              // If line point is close to bar top, position label away from the bar
              if (overlap.pixelDistance < 75) {
                // If line is above or at same level as bar, place label below the dot
                // If line is clearly below bar top, keep label above the dot (away from bar label)
                return overlap.lineIsAboveBar ? "bottom" : "top";
              }
            }

            // Default: place above the dot
            return "top";
          }

          const value = currentDataLabelValue(context);
          if (value === null) {
            return "end";
          }

          const globalMax = globalMaxValue(context);
          return value > globalMax * 0.8 && !hasNarrowBars ? "start" : "end";
        },
        offset: function (context: DataLabelContext) {
          if (chartDataset(context).type === "line") {
            // Calculate distance from bar top to determine offset
            const overlap = lineOverlapAtIndex(context);
            if (overlap) {
              // Increase offset when line point is close to bar top
              if (overlap.pixelDistance < 30) {
                return 26; // Very close - push label far away
              } else if (overlap.pixelDistance < 75) {
                return 20; // Moderately close - medium push
              }
            }

            return 8; // Default offset for line labels
          }

          const value = currentDataLabelValue(context);
          if (value === null) {
            return 2;
          }

          const globalMax = globalMaxValue(context);
          return value > globalMax * 0.8 && !hasNarrowBars ? 5 : 2;
        },
      },
    },
  } as const;

  const data = {
    labels,
    datasets,
  };

  // Only use flex layout when height is h-full (for containers that need to fill parent)
  const useFlexLayout = height === "h-full";

  return (
    <div className={`w-full ${useFlexLayout ? "h-full flex flex-col" : ""}`}>
      {title && (
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
          {title}
        </h3>
      )}
      <div className={`${height} w-full ${useFlexLayout ? "flex-1" : ""}`}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};
